import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { useT } from "../../context/locale";
import { Button, cx } from "./index";
import { DateField, NumberField, SelectField, TextField } from "./form";

/**
 * Filter bar and the state behind it.
 *
 * Filters are **staged**: typing edits a draft, and nothing reaches the API until
 * Search is pressed. An earlier version re-fetched on every keystroke, which put
 * a request per character on the wire and made the table flicker while an
 * operator was still deciding what to look for.
 */

export type FilterValues = Record<string, string>;

export interface FilterState {
  /** What the inputs show — edits land here and go no further. */
  draft: FilterValues;
  /** What the last Search applied. Feed this to the query. */
  applied: FilterValues;
  set: (key: string, value: string) => void;
  apply: () => void;
  reset: () => void;
  dirty: boolean;
  activeCount: number;
}

export function useFilters(
  initial: FilterValues,
  onApply?: () => void,
): FilterState {
  const [draft, setDraft] = useState<FilterValues>(initial);
  const [applied, setApplied] = useState<FilterValues>(initial);

  const set = useCallback((key: string, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const apply = useCallback(() => {
    setApplied(draft);
    onApply?.();
  }, [draft, onApply]);

  const reset = useCallback(() => {
    setDraft(initial);
    setApplied(initial);
    onApply?.();
    // `initial` is a literal at every call site, so re-running on identity is noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApply]);

  const dirty = useMemo(
    () =>
      Object.keys(draft).some(
        (key) => (draft[key] ?? "") !== (applied[key] ?? ""),
      ),
    [draft, applied],
  );

  const activeCount = useMemo(
    () =>
      Object.values(applied).filter(
        (value) => value !== "" && value !== undefined,
      ).length,
    [applied],
  );

  return { draft, applied, set, apply, reset, dirty, activeCount };
}

export interface FilterDef {
  key: string;
  label: string;
  type?: "text" | "select" | "date" | "number";
  /**
   * Shown inside the empty control — and the only place a filter may explain
   * itself.
   *
   * There is deliberately no `hint`. The row is laid out with `items-end`, so a
   * line of help text under one control pushes that control up while its
   * neighbours stay put, and the bar acquires a step in the middle of it. The
   * Draws bar had exactly that: "From the house's side" under Round result,
   * with the select sitting a line higher than Status beside it. The sentence
   * now lives in that filter's own default option, where it reads the same and
   * costs no height.
   */
  placeholder?: string;
  options?: { value: string; label: string }[];
}

/**
 * A bar above the table rather than a rail beside it: the table is the thing
 * being read, and it should have the full width to do it in.
 *
 * The controls wrap in a responsive grid, so a long filter set grows downward in
 * even columns instead of spilling into a ragged line.
 */
export function FilterBar({
  filters,
  state,
  loading,
  children,
}: {
  filters: FilterDef[];
  state: FilterState;
  loading?: boolean;
  children?: ReactNode;
}) {
  const t = useT();

  return (
    <form
      className="panel p-4"
      onSubmit={(event) => {
        event.preventDefault();
        state.apply();
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <p className="eyebrow">{t("common.filters")}</p>
        {state.activeCount > 0 ? (
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{ background: "var(--accent)", color: "var(--bg-deep)" }}
          >
            {t("common.filtersActive", { count: state.activeCount })}
          </span>
        ) : null}
        {state.dirty ? (
          <span
            className="text-[11px] font-semibold"
            style={{ color: "var(--warning)" }}
          >
            {t("common.filtersUnapplied")}
          </span>
        ) : null}
      </div>

      <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {filters.map((filter) => (
          <FilterControl key={filter.key} filter={filter} state={state} />
        ))}

        {children}

        <div className="flex gap-2">
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1"
          >
            {t("action.search")}
          </Button>
          <Button type="button" variant="ghost" onClick={state.reset}>
            {t("action.reset")}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** One filter, rendered with the shared control for its type. */
function FilterControl({
  filter,
  state,
}: {
  filter: FilterDef;
  state: FilterState;
}) {
  const value = state.draft[filter.key] ?? "";
  const set = (next: string) => state.set(filter.key, next);

  if (filter.type === "select") {
    return (
      <SelectField
        label={filter.label}
        value={value}
        onChange={set}
        options={filter.options ?? []}
      />
    );
  }
  if (filter.type === "date") {
    return (
      <DateField
        label={filter.label}
        value={value}
        onChange={set}
      />
    );
  }
  if (filter.type === "number") {
    return (
      <NumberField
        label={filter.label}
        value={value}
        onChange={set}
        placeholder={filter.placeholder}
      />
    );
  }
  return (
    <TextField
      label={filter.label}
      value={value}
      onChange={set}
      placeholder={filter.placeholder}
    />
  );
}

/* ── Sorting ──────────────────────────────────────────────────────────────── */

export interface SortState {
  sort: string;
  order: "asc" | "desc";
  /** Click a column: same column flips direction, a new one starts descending. */
  toggle: (field: string) => void;
}

export function useSort(
  defaultSort: string,
  defaultOrder: "asc" | "desc" = "desc",
  onChange?: () => void,
): SortState {
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState<"asc" | "desc">(defaultOrder);

  const toggle = useCallback(
    (field: string) => {
      if (field === sort)
        setOrder((current) => (current === "asc" ? "desc" : "asc"));
      else {
        setSort(field);
        setOrder("desc");
      }
      onChange?.();
    },
    [sort, onChange],
  );

  return { sort, order, toggle };
}

/**
 * A sortable column heading.
 *
 * The arrow only appears on the active column; showing a neutral glyph on every
 * header turns a table into a row of decoration and makes the one that matters
 * harder to spot.
 */
export function SortHeader({
  field,
  state,
  children,
  align = "left",
}: {
  field: string;
  state: SortState;
  children: ReactNode;
  align?: "left" | "right";
}) {
  const t = useT();
  const active = state.sort === field;
  return (
    <button
      type="button"
      onClick={() => state.toggle(field)}
      className={cx(
        "eyebrow group inline-flex w-full items-center gap-1 transition-colors hover:text-[var(--accent)]",
        align === "right" && "justify-end",
      )}
      style={active ? { color: "var(--accent)" } : undefined}
      aria-sort={
        active ? (state.order === "asc" ? "ascending" : "descending") : "none"
      }
      title={t("common.sortBy", {
        field: typeof children === "string" ? children : field,
      })}
    >
      {children}
      <span
        aria-hidden
        className={cx(
          "text-[9px]",
          active ? "opacity-100" : "opacity-0 group-hover:opacity-40",
        )}
      >
        {active && state.order === "asc" ? "▲" : "▼"}
      </span>
    </button>
  );
}

/* ── Pagination ───────────────────────────────────────────────────────────── */

const PAGE_SIZES = [10, 25, 50, 100];

export function PageBar({
  page,
  pages,
  total,
  perPage,
  onPage,
  onPerPage,
}: {
  page: number;
  pages: number;
  total: number;
  perPage: number;
  onPage: (page: number) => void;
  onPerPage: (perPage: number) => void;
}) {
  const t = useT();
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-dim text-[12px]">
          {total === 0 ? (
            t("common.noRecords")
          ) : (
            <>
              {t("common.showing")}{" "}
              <span className="tnum text-body font-semibold">{from}</span>–
              <span className="tnum text-body font-semibold">{to}</span>{" "}
              {t("common.of")}{" "}
              <span className="tnum text-body font-semibold">
                {total.toLocaleString()}
              </span>
            </>
          )}
        </p>

        <label className="flex items-center gap-2">
          <span className="eyebrow">{t("common.perPage")}</span>
          <select
            value={perPage}
            onChange={(event) => onPerPage(Number(event.target.value))}
            className="h-8 cursor-pointer rounded-lg border px-2 text-[12px] outline-none"
            style={{
              background: "var(--surface-inset)",
              borderColor: "var(--border)",
              color: "var(--text)",
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1.5">
        <PageButton
          disabled={page <= 1}
          onClick={() => onPage(1)}
          label={t("common.first")}
        >
          «
        </PageButton>
        <PageButton
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          label={t("common.previous")}
        >
          ‹
        </PageButton>
        <span className="px-2 text-[12px]">
          {t("common.page")} <span className="tnum font-semibold">{page}</span>{" "}
          {t("common.of")}{" "}
          <span className="tnum font-semibold">{Math.max(1, pages)}</span>
        </span>
        <PageButton
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          label={t("common.next")}
        >
          ›
        </PageButton>
        <PageButton
          disabled={page >= pages}
          onClick={() => onPage(pages)}
          label={t("common.last")}
        >
          »
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  disabled,
  onClick,
  children,
  label,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cx(
        "grid size-8 place-items-center rounded-lg border text-[14px] transition-colors",
        disabled
          ? "cursor-not-allowed opacity-35"
          : "hover:bg-[var(--surface-hover)]",
      )}
      style={{
        borderColor: "var(--border)",
        background: "var(--surface-inset)",
        color: "var(--text)",
      }}
    >
      {children}
    </button>
  );
}
